/* eslint-disable @typescript-eslint/no-explicit-any */
import type { HttpErrorResponse } from '@angular/common/http';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Subject, Subscription } from 'rxjs';
import { getValidationConfigFromCardNo } from 'src/app/shared/helpers/card.helper';
import { IProviderGroup } from 'src/app/shared/interfaces/provider/provider-group.interfaces';
import {
  type IProviderStatusDates,
  providerStatusDatesExtractor,
} from 'src/app/shared/interfaces/provider/provider.interfaces';
import { AccountGQLService } from 'src/app/shared/services/account/account.graphql.service';
import { AccountService } from 'src/app/shared/services/account/account.service';
import { DatepickerService } from 'src/app/shared/services/datepicker.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { ProviderGroupService } from 'src/app/shared/services/provider/provider-group.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';
import {
  DEFAULT_MODAL_OPTIONS,
  EMAIL_REGEX,
} from 'src/app/shared/variables/variables';

import { CommonGraphqlService } from '../../../services/common.graphql.service';
import { CreateProviderGroupModalComponent } from '../../provider/create-provider-group-modal/create-provider-group-modal.component';
import { CreateProviderModalComponent } from '../../provider/create-provider-modal/create-provider-modal.component';
import {
  IAccount,
  IAccountAPI,
  IApplication,
} from './../../../interfaces/account/account.interface';
import { AlertService } from './../../../services/alert.service';
import { DropDownItem } from './../../filters/filter.interface';

@Component({
  selector: 'app-create-account-modal',
  templateUrl: './create-account-modal.component.html',
})
export class CreateAccountModalComponent implements OnInit, OnDestroy {
  @Input() accountNumber: string;
  @Input() createdBy: string;

  account?: IAccount;
  applications: IApplication[];
  states: DropDownItem[];

  providerGroups?: IProviderGroupDTO[];

  accountForm: UntypedFormGroup;
  isEditMode?: boolean;
  isViewMode: boolean;
  isClientAdmin?: boolean;

  isLoading = true;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private modal: NgbModal,
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private accountGQLService: AccountGQLService,
    private accountService: AccountService,
    private providerGroupService: ProviderGroupService,
    private alertService: AlertService,
    private modalService: ModalService,
    private commonGraphqlService: CommonGraphqlService,
    private datepickerService: DatepickerService,
    private userStateService: UserStateService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.isViewMode = true;
    this.isEditMode = !!this.accountNumber;

    const currentUser = this.userStateService.user;
    const permissions = currentUser?.permissions;

    const containsAccounts = permissions?.some((permission) => {
      return (
        permission.name === 'Accounts' ||
        permission.name === 'Client Admin - Accounts'
      );
    });
    this.isClientAdmin = permissions?.some((permission) => {
      return permission.name === 'Client Admin - Accounts';
    });
    this.isViewMode = !containsAccounts;

    this.accountForm = this.fb.group({
      accountNumber: [
        { value: '', disabled: this.isEditMode },
        [Validators.required],
      ],
      name: ['', [Validators.required]],
      pms: [
        { value: '', disabled: this.isEditMode && this.isClientAdmin },
        [Validators.required],
      ],
      contact: this.fb.group({
        name: [''],
        phone: ['', [Validators.maxLength(10)]],
        fax: [''],
        email: ['', Validators.pattern(EMAIL_REGEX)],
      }),
      address: this.fb.group({
        line1: [''],
        line2: [''],
        zipcode: [''],
        state: [''],
        city: [''],
      }),
      ecsDate: [
        {
          value: this.datepickerService.formatDate(new Date()),
          disabled: true,
        },
      ],
      ecsStatus: ['A'],
      eraDate: [
        {
          value: this.datepickerService.formatDate(new Date()),
          disabled: true,
        },
      ],
      eraStatus: ['A'],
      claimStatusDate: [{ value: '', disabled: true }],
      claimStatusStatus: ['I'],
      eligibilityDate: [{ value: '', disabled: true }],
      eligibilityStatus: ['I'],
      statementDate: [{ value: '', disabled: true }],
      statementStatus: ['I'],
      expirationDate: [''],
      dateSetup: [{ value: '', disabled: true }],
      dateLastDbUpdated: [{ value: '', disabled: true }],
      dateTerminated: [''],
      creditCardNo: [{ value: '', disabled: false }, [Validators.minLength(4)]],
      creditCardExport: [''],
      cvvCode: [''],
      taxExempt: [''],
      terminateReason: [''],
      terminateNoticies: [''],
      notes: [''],
      lastDbUpdatedBy: [{ value: '', disabled: true }],
    });

    const requests = this.accountNumber
      ? [
          this.commonGraphqlService.getApplications(),
          this.accountGQLService.getAccount(this.accountNumber),
        ]
      : [this.commonGraphqlService.getApplications()];

    const accountEntities = combineLatest(requests);

    this.subscriptions.add(
      accountEntities.subscribe({
        next: ([applications, account]) => {
          this.isLoading = false;

          this.account = this.accountNumber ? (account as IAccount) : undefined;
          this.applications = applications as IApplication[];

          const expirationDate =
            this.account?.expirationYear && this.account.expirationMonth
              ? new Date(
                  +this.account.expirationYear,
                  +this.account.expirationMonth,
                  1,
                )
              : undefined;

          if (this.account) {
            this.account.providerGroups = this.account.providerGroups?.filter(
              (g) => {
                return g.isActive;
              },
            );
            this.providerGroups = this.account.providerGroups?.map((x) => {
              return {
                ...x,
                isExpanded: false,
              };
            });

            this.accountForm.patchValue({
              accountNumber: this.account.accountNumber,
              pms: this.account.pMS,
              name: this.account.name,
              address: {
                line1: this.account.addressLine1,
                line2: this.account.addressLine2,
                city: this.account.addressCity,
                state: this.account.addressState,
                zipcode: this.account.addressZipcode,
              },
              contact: {
                name: this.account.contactName,
                phone: this.account.contactPhone,
                fax: this.account.contactFax,
                email: this.account.contactEmail,
              },
              ecsStatus: this.account.eCSStatus,
              eraStatus: this.account.eRAStatus,
              claimStatusStatus: this.account.claimStatusStatus,
              eligibilityStatus: this.account.eligibilityStatus,
              statementStatus: this.account.statementStatus,
              notes: this.account.notes,
              gemsSetupFlag: this.account.gemsSetupFlag === 'Y',
              creditCardNo: this.account.creditCardNo,
              expirationMonth: this.account.expirationMonth,
              expirationYear: this.account.expirationYear,
              creditCardExport: this.account.creditCardExport === 'Y',
              taxExempt: this.account.taxExempt,
              terminateReason: this.account.terminateReason,
              lastDbUpdatedBy: this.account.lastDbUpdatedBy,
              ecsDate: this.datepickerService.formatDate(
                this.account.eCSDate ?? new Date(),
              ),
              eraDate: this.datepickerService.formatDate(
                this.account.eRADate ?? new Date(),
              ),
              claimStatusDate: this.datepickerService.formatDate(
                this.account.claimStatusDate ?? new Date(),
              ),
              eligibilityDate: this.datepickerService.formatDate(
                this.account.eligibilityDate ?? new Date(),
              ),
              statementDate: this.datepickerService.formatDate(
                this.account.statementDate ?? new Date(),
              ),
              expirationDate: this.datepickerService.formatDate(
                expirationDate ?? new Date(),
              ),
              dateSetup: this.datepickerService.formatDate(
                this.account.dateSetup ?? new Date(),
              ),
              dateLastDbUpdated: this.datepickerService.formatDate(
                this.account.dateLastDbUpdated ?? new Date(),
              ),
              dateTerminated: this.datepickerService.formatDate(
                this.account.dateTerminated ?? new Date(),
              ),
            });

            if (this.accountForm.invalid) {
              this.accountForm.markAllAsTouched();
            }
          }
        },
        error: (_error) => {
          this.isLoading = false;
        },
      }),
    );

    if (this.isViewMode) {
      this.accountForm.disable();
    }
  }

  cardMaskFunction(rawValue: string): RegExp[] {
    const card = getValidationConfigFromCardNo(rawValue);
    if (card) {
      return card.mask;
    }
    return [/\d/];
  }

  loadProviderGroups() {
    this.subscriptions.add(
      this.accountGQLService.getAccount(this.accountNumber).subscribe({
        next: (account) => {
          // @ts-expect-error - account is unknown type (TODO: Fix this)
          this.account.providerGroups = account?.providerGroups.filter((g) => {
            return g.isActive;
          });
          this.providerGroups = this.account?.providerGroups?.map((x) => {
            return {
              ...x,
              isExpanded: false,
            };
          });
        },
        error: (error: unknown) => {
          this.isLoading = false;
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
        },
      }),
    );
  }

  onCheckboxStatusChanged($event: any, fieldName: string) {
    const date = this.datepickerService.formatDate(new Date());
    const formField = this.accountForm.get(fieldName);

    if (formField) {
      if ($event.target.checked) {
        formField.setValue(date);
      } else {
        formField.setValue('');
      }
    }
  }

  getFormValue(name: string): UntypedFormControl {
    return this.accountForm.get(name) as UntypedFormControl;
  }

  close() {
    this.activeModal.dismiss();
  }

  saveAccountForm() {
    if (this.isViewMode) {
      this.activeModal.close();
    } else {
      this.subscriptions.add(
        this.saveAccount().subscribe((result) => {
          this.activeModal.close(result);
        }),
      );
    }
  }

  saveAccount(): Subject<IAccount> {
    const mResult = new Subject<IAccount>();
    if (this.accountForm.valid) {
      this.isLoading = true;

      const accountFormValue = this.accountForm.getRawValue();

      const account = {
        accountNumber:
          accountFormValue.accountNumber ?? this.account?.accountNumber,
        pms: accountFormValue.pms,
        name: accountFormValue.name,
        address: {
          line1: accountFormValue.address.line1,
          line2: accountFormValue.address.line2,
          city: accountFormValue.address.city,
          state: accountFormValue.address.state,
          zipcode: accountFormValue.address.zipcode,
        },
        contact: {
          name: accountFormValue.contact.name,
          phone: accountFormValue.contact.phone,
          fax: accountFormValue.contact.fax,
          email: accountFormValue.contact.email,
        },
        ecsStatus: accountFormValue.ecsStatus,
        eraStatus: accountFormValue.eraStatus,
        claimStatusStatus: accountFormValue.claimStatusStatus,
        eligibilityStatus: accountFormValue.eligibilityStatus,
        statementStatus: accountFormValue.statementStatus,
        notes: accountFormValue.notes,
        gemsSetupFlag: undefined,
        creditCardNo: (accountFormValue.creditCardNo || '').replaceAll(' ', ''),
        expirationMonth: accountFormValue.expirationDate?.month,
        expirationYear: accountFormValue.expirationDate?.year,
        creditCardExport: 'N',
        taxExempt: accountFormValue.taxExempt,
      } as IAccountAPI;

      const request = this.isEditMode
        ? this.accountService.editAccount(account)
        : this.accountService.createAccount(account);

      this.subscriptions.add(
        request.subscribe({
          next: (result) => {
            if (!this.isEditMode) {
              this.account = result as IAccount;
              this.accountNumber = this.account.accountNumber;
              this.accountForm.patchValue({
                ...result,
                ecsStatus: result.ecsDate ? 'A' : 'I',
                eraStatus: result.eraDate ? 'A' : 'I',
                claimStatusStatus: result.claimStatusDate ? 'A' : 'I',
                eligibilityStatus: result.eligibilityDate ? 'A' : 'I',
                statementStatus: result.statementDate ? 'A' : 'I',

                ecsDate: this.datepickerService.formatDate(
                  result.ecsDate as Date,
                ),
                eraDate: this.datepickerService.formatDate(
                  result.eraDate as Date,
                ),
                claimStatusDate: this.datepickerService.formatDate(
                  result.claimStatusDate as Date,
                ),
                eligibilityDate: this.datepickerService.formatDate(
                  result.eligibilityDate as Date,
                ),
                statementDate: this.datepickerService.formatDate(
                  result.statementDate as Date,
                ),
              } as IAccountAPI);

              this.accountForm.get('accountNumber')?.disable();
            }

            this.accountForm.markAsUntouched();
            this.alertService.success(
              'Edit account',
              'Account has been successfully modified',
            );

            if (this.account) {
              mResult.next(this.account);
            }
            this.isLoading = false;
          },
          error: (error: unknown) => {
            this.isLoading = false;
            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
          },
        }),
      );

      return mResult;
    }

    return mResult;
  }

  editProviderGroupClick(groupId?: string) {
    if (
      (this.isEditMode &&
        this.accountForm.touched &&
        this.accountForm.invalid) ||
      (!this.isEditMode && this.accountForm.invalid)
    ) {
      this.accountForm.markAllAsTouched();
      this.alertService.warning(
        'Validation Message',
        'Some inputs are invalid, please fix form data and try again.',
      );
      return;
    }

    if (this.accountForm.touched) {
      this.subscriptions.add(
        this.saveAccount().subscribe(() => {
          this.editGroup(groupId);
        }),
      );
    } else {
      this.editGroup(groupId);
    }
  }

  editGroup(groupId?: string) {
    const modal = this.modal.open(CreateProviderGroupModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'xl',
    });

    const mi = modal.componentInstance as CreateProviderGroupModalComponent;
    const accountFormValue: IProviderStatusDates =
      this.accountForm.getRawValue();

    if (groupId) {
      mi.groupId = groupId;
    }

    if (this.account) {
      mi.account = {
        ...this.account,
        pMS: this.account.pMS,
      };
    }

    if (!groupId) {
      mi.statusDates = providerStatusDatesExtractor(accountFormValue);
    }

    modal.result
      .then((result) => {
        console.log('editGroup:', result);
        this.loadProviderGroups();
      })
      .catch((error: unknown) => {
        if (error) {
          this.isLoading = false;
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  async deleteGroup(groupId?: string) {
    const modalText = {
      bodyTitle: 'Are you sure you want to delete this group?',
      bodyText: 'This will remove group from the selected account.',
    };

    await this.modalService.open('confirm', modalText).result;

    this.isLoading = true;

    if (groupId) {
      this.subscriptions.add(
        this.providerGroupService.deleteProviderGroup(groupId).subscribe({
          next: () => {
            this.alertService.success(
              'Delete Provider Group',
              'Provider Group has been successfully deleted',
            );
            this.isLoading = false;
            this.loadProviderGroups();
          },
          error: (error: unknown) => {
            this.isLoading = false;
            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
          },
        }),
      );
    }
  }

  addProvider() {
    this.editProvider();
  }

  editProvider() {
    const modal = this.modal.open(CreateProviderModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'xl',
    });

    modal.result
      .then(() => {
        this.loadProviderGroups();
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }
}

type IProviderGroupDTO = {
  isExpanded: boolean;
} & IProviderGroup;
